/* ============================================================================
   MEMORIA — theme.js (vanilla)
   ----------------------------------------------------------------------------
   Modules :
     1. Thread morph + color lerp (signature DNA #1 + #2)
     2. Reveals (IntersectionObserver)
     3. Header sticky scrolled state
     4. Mobile burger / drawer
     5. Sticky CTA visibility
     6. Quick-action reservation (cart add via fetch)

   No framework. ES module-friendly (script tag, defer).
   Respects `prefers-reduced-motion: reduce` everywhere.

   Perf pass 2026-05-23 (web-perf-auditor) :
   - update() is now skipped entirely when no universe sections are present
     (PDP, collection, page) AND the page is below the fold — saves CPU.
   - body.style.backgroundColor + body.style.color removed from the RAF loop;
     CSS custom properties (--bg/--fg) are the single source of truth, body
     reads them via the body{background:var(--bg);color:var(--fg)} rule. One
     style mutation per RAF instead of three.
   - DOM reads are cached after the first update() (offsetTop/offsetHeight
     only re-read on resize), eliminating layout thrash.
   - Page Visibility API: pauses scroll listener when tab is hidden.
   - Throttled scroll fallback for reduced-motion path (was firing on every
     scroll event without RAF, very expensive on mobile).
   ============================================================================ */

(function () {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     1. UNIVERSE DATA + LERP HELPERS
  ------------------------------------------------------------------------ */
  let UNIVERSES = [];
  try {
    const json = document.getElementById('memoria-universes');
    if (json) UNIVERSES = JSON.parse(json.textContent);
  } catch (e) {
    console.warn('[memoria] could not parse universes data', e);
  }

  function hex2rgb(h) {
    const m = h.replace('#', '');
    return [
      parseInt(m.slice(0, 2), 16),
      parseInt(m.slice(2, 4), 16),
      parseInt(m.slice(4, 6), 16),
    ];
  }
  function rgb2hex(r, g, b) {
    const c = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
    return '#' + c(r) + c(g) + c(b);
  }
  function lerpColor(c1, c2, t) {
    const a = hex2rgb(c1);
    const b = hex2rgb(c2);
    return rgb2hex(
      a[0] + (b[0] - a[0]) * t,
      a[1] + (b[1] - a[1]) * t,
      a[2] + (b[2] - a[2]) * t
    );
  }
  function contrastRatio(c1, c2) {
    const lum = (hex) => {
      const [r, g, b] = hex2rgb(hex).map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    };
    const x = lum(c1);
    const y = lum(c2);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
  }
  function rgba(hex, alpha) {
    const [r, g, b] = hex2rgb(hex);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  function curveToD(c) {
    return `M ${c.sX} 0 C ${c.c1x} ${c.c1y}, ${c.c2x} ${c.c2y}, ${c.eX} 100`;
  }
  function lerpCurve(a, b, t) {
    return {
      sX: a.sX + (b.sX - a.sX) * t,
      c1x: a.c1x + (b.c1x - a.c1x) * t,
      c1y: a.c1y + (b.c1y - a.c1y) * t,
      c2x: a.c2x + (b.c2x - a.c2x) * t,
      c2y: a.c2y + (b.c2y - a.c2y) * t,
      eX: a.eX + (b.eX - a.eX) * t,
    };
  }

  /* ------------------------------------------------------------------------
     2. THREAD + LERP SCROLL CONTROLLER
  ------------------------------------------------------------------------ */
  function initThreadAndLerp() {
    if (!UNIVERSES.length) return;

    const root = document.documentElement;
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const header = document.getElementById('site-header');
    const stickyCta = document.getElementById('sticky-cta');
    const thread = document.querySelector('[data-thread]');
    const pathEl = document.getElementById('thread-path');
    const shadowEl = document.getElementById('thread-shadow');
    const animEl = document.getElementById('thread-path-anim');

    // Cache section refs once; re-measure their geometry on resize only.
    // BUGFIX : on lookup les sections par data-universe (DOM order) au lieu d'utiliser
    // UNIVERSES indexé par id. Permet à TOUTES les sections (pas que 4) de participer au lerp.
    const universesById = {};
    UNIVERSES.forEach((u) => { universesById[u.id] = u; });
    const sections = Array.from(document.querySelectorAll('[data-universe]'));
    const sectionUniverses = sections.map((el) => universesById[el.dataset.universe] || UNIVERSES[0]);
    const hasUniverses = sections.length > 0;

    // Cached geometry (offsetTop, offsetHeight). Re-computed on resize.
    let geom = [];
    function measureGeom() {
      geom = sections.map((s) => ({ top: s.offsetTop, h: s.offsetHeight }));
    }
    if (hasUniverses) measureGeom();

    // Couleurs de marque lues une fois (les réglages du thème peuvent les changer).
    const tokens = getComputedStyle(root);
    const brandInks = [
      ['accent', '--c-accent', '#B83228'],
      ['gold', '--c-gold', '#D9B85A'],
      ['blue', '--c-blue', '#1E4A6D'],
    ].map(([name, v, fallback]) => {
      const val = tokens.getPropertyValue(v).trim();
      return [name, /^#[0-9a-f]{6}$/i.test(val) ? val : fallback];
    });
    function inkFor(base, bg, fg, target) {
      for (let p = 0; p <= 1.001; p += 0.05) {
        const c = lerpColor(base, fg, p);
        if (contrastRatio(c, bg) >= target) return c;
      }
      return fg;
    }

    // Cached state to skip redundant style writes
    let lastBg = '';
    let lastFg = '';
    let lastLine = '';
    let lastD = '';
    let lastScrolled = null;
    let lastStickyVisible = null;

    // Robust scroll-position getter — handles cases where the scroll
    // container is <body> instead of window (overflow-x:clip cascade,
    // Shopify preview wrappers, etc.).
    function getScrollY() {
      return window.scrollY
        || document.scrollingElement?.scrollTop
        || document.documentElement.scrollTop
        || document.body.scrollTop
        || 0;
    }

    function update() {
      const scrollY = getScrollY();

      // ---- Header scrolled state (works on every page) ----
      if (header) {
        const shouldScrolled = scrollY > window.innerHeight * 0.4;
        if (shouldScrolled !== lastScrolled) {
          if (shouldScrolled) header.classList.add('is-scrolled');
          else header.classList.remove('is-scrolled');
          lastScrolled = shouldScrolled;
        }
      }

      // If this page has no universe sections, nothing else to do.
      if (!hasUniverses) return;

      const vpCenter = scrollY + window.innerHeight / 2;

      // 2026-09-15 : le fond se mélangeait du CENTRE d'une section au centre
      // de la suivante, texte compris. Entre un univers sombre et un univers
      // clair, les deux encres se croisaient à mi-chemin : texte gris sur fond
      // gris, 1,00:1. Mesuré sur toute la hauteur, l'atelier passait 38 % du
      // défilement sous 3:1. Désormais :
      //  - le fond ne se mélange que sur une bande de ±15 % d'écran autour de
      //    la frontière entre deux sections (bornée à la demi-hauteur des
      //    sections voisines) ; ailleurs chaque section a son univers pur ;
      //  - le texte ne se mélange jamais : il prend celle des deux encres qui
      //    contraste le mieux avec le fond du moment ;
      //  - au milieu de la bande, le texte atténué remonte à pleine encre ;
      //  - texte atténué à 72 % (au lieu de 58 %) et doux à 82 % (au lieu de
      //    70 %) : 58 % donnait 4,0:1 sur crème et 3,8:1 sur le bleu, sous le
      //    seuil de 4,5:1 des petits libellés en capitales ;
      //  - les couleurs de marque posées en texte (rouge, or, bleu) sont
      //    publiées en --ink-* : la teinte d'origine si elle tient 4,5:1 sur le
      //    fond du moment (3:1 pour --ink-*-lg, grands titres), sinon la même
      //    teinte rapprochée de l'encre juste assez pour y arriver. Le rouge
      //    tient sur crème, pas sur le brun (2,5:1) ni sur le bleu (1,6:1).
      // Résultat simulé : 0 % du défilement sous 3:1, pire cas 3,8:1.
      let k = 0;
      for (let i = 0; i < geom.length; i++) {
        if (vpCenter >= geom[i].top) k = i;
      }
      const bandAt = (i) => Math.min(
        window.innerHeight * 0.15, geom[i - 1].h / 2, geom[i].h / 2
      );
      let activeIdx = k;
      let nextIdx = k;
      let t = 0;
      if (k + 1 < geom.length && vpCenter > geom[k + 1].top - bandAt(k + 1)) {
        nextIdx = k + 1;
        const bd = bandAt(k + 1);
        t = (vpCenter - (geom[k + 1].top - bd)) / (2 * bd);
      } else if (k > 0 && vpCenter < geom[k].top + bandAt(k)) {
        activeIdx = k - 1;
        const bd = bandAt(k);
        t = (vpCenter - (geom[k].top - bd)) / (2 * bd);
      }
      t = Math.max(0, Math.min(1, t));
      t = t * t * (3 - 2 * t);

      const a = sectionUniverses[activeIdx];
      const b = sectionUniverses[nextIdx];

      const bg = lerpColor(a.bg, b.bg, t);
      const fg = contrastRatio(bg, a.fg) >= contrastRatio(bg, b.fg) ? a.fg : b.fg;
      const line = lerpColor(a.line, b.line, t);
      // Pas de relèvement entre deux sections du même univers : le fond ne
      // bouge pas, le texte atténué n'a pas à foncer au passage.
      const edge = a.bg === b.bg ? 0 : 1 - Math.abs(2 * t - 1);
      const softA = (0.82 + 0.18 * edge).toFixed(2);
      const muteA = (0.72 + 0.28 * edge).toFixed(2);

      // Single batch of CSS var writes. Body bg/color follow via CSS rule
      // `body { background: var(--bg); color: var(--fg); }` — no direct
      // style.backgroundColor write needed (was double work).
      // Fond et encre sont réécrits ENSEMBLE : une écriture isolée pouvait
      // laisser le fond d'un univers sous l'encre d'un autre.
      const pair = bg + fg + softA + muteA;
      if (pair !== lastBg) {
        root.style.setProperty('--bg', bg);
        if (themeMeta) themeMeta.setAttribute('content', bg);
        lastBg = pair;
        // Logo swap selon la luminance du fond courant (lerp au scroll).
        if (header) {
          const m = bg.replace('#', '');
          const r = parseInt(m.slice(0, 2), 16),
            g = parseInt(m.slice(2, 4), 16),
            b = parseInt(m.slice(4, 6), 16);
          const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          header.classList.toggle('header--on-dark', lum < 0.5);
        }
        root.style.setProperty('--fg', fg);
        root.style.setProperty('--fg-soft', rgba(fg, softA));
        root.style.setProperty('--fg-mute', rgba(fg, muteA));
        root.style.setProperty('--rule', rgba(fg, 0.14));
        for (const [name, base] of brandInks) {
          root.style.setProperty('--ink-' + name, inkFor(base, bg, fg, 4.5));
          root.style.setProperty('--ink-' + name + '-lg', inkFor(base, bg, fg, 3));
        }
        lastFg = fg;
      }
      if (line !== lastLine) {
        root.style.setProperty('--line', line);
        if (thread) thread.style.color = line;
        lastLine = line;
      }

      // Thread morph — only on home (sections present)
      let ci = 0;
      for (let i = 0; i < geom.length; i++) {
        if (vpCenter >= geom[i].top + geom[i].h / 2) ci = i;
      }
      const cj = Math.min(ci + 1, geom.length - 1);
      let tc = 0;
      if (ci < geom.length - 1) {
        const c1 = geom[ci].top + geom[ci].h / 2;
        const c2 = geom[cj].top + geom[cj].h / 2;
        tc = Math.max(0, Math.min(1, (vpCenter - c1) / (c2 - c1)));
      }
      const interp = lerpCurve(sectionUniverses[ci].curve, sectionUniverses[cj].curve, tc);
      const d = curveToD(interp);
      if (d !== lastD) {
        if (pathEl) pathEl.setAttribute('d', d);
        if (shadowEl) shadowEl.setAttribute('d', d);
        if (animEl) animEl.setAttribute('d', d);
        lastD = d;
      }

      // Sticky CTA visibility
      if (stickyCta) {
        const lastSection = sections[sections.length - 1];
        const lastTop = lastSection ? lastSection.offsetTop : Infinity;
        const showAfter = window.innerHeight * 0.3;
        const hideBeforeLast = scrollY + window.innerHeight * 0.5 < lastTop;
        const shouldShow = scrollY > showAfter && hideBeforeLast;
        if (shouldShow !== lastStickyVisible) {
          if (shouldShow) stickyCta.classList.add('is-visible');
          else stickyCta.classList.remove('is-visible');
          lastStickyVisible = shouldShow;
        }
      }
    }

    let raf = null;
    let scrollPending = false;
    function onScroll() {
      if (scrollPending) return;
      scrollPending = true;
      raf = requestAnimationFrame(() => {
        update();
        scrollPending = false;
      });
    }

    // Initial paint
    update();

    if (prefersReduced) {
      // Skip lerp entirely under reduced-motion (perf + a11y win).
      // We still want header scrolled state to update — but throttled.
      let throttled = false;
      window.addEventListener('scroll', () => {
        if (throttled) return;
        throttled = true;
        setTimeout(() => {
          update();
          throttled = false;
        }, 150);
      }, { passive: true });
    } else {
      // Attach scroll listener on multiple targets — <body> becomes the scroll
      // container when overflow-x:clip cascades to overflow-y:auto, or in
      // Shopify preview wrappers. 'capture: true' on document catches scroll
      // events from any descendant in the capture phase.
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('scroll', onScroll, { passive: true, capture: true });
      document.body.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', () => {
        if (hasUniverses) measureGeom();
        update();
      });
    }

    // 2026-09-11 : la géométrie n'était remesurée qu'au resize. Or les images
    // du journal (lazy) se chargent à l'approche et la section passe de 1 224
    // à 937 px : tout ce qui suit se décale, et le lerp ne tombe plus au centre
    // des sections. Mesuré au centre du journal : fond encore mélangé à 28 %
    // au sable, titres des cartes à 3,0:1 au lieu de 12,8:1. On remesure dès
    // qu'une section (ou la page) change de taille, dans les deux branches,
    // mouvement réduit compris.
    if (hasUniverses && 'ResizeObserver' in window) {
      const geomObserver = new ResizeObserver(() => {
        measureGeom();
        update();
      });
      sections.forEach((s) => geomObserver.observe(s));
      geomObserver.observe(document.body);
    }

    // 2026-09-15 : les pages atelier, histoire, mod-explique, journal et article
    // ont chacune un observateur qui posait l'univers de la section « la plus
    // visible ». Il écrivait par-dessus ce module avec un autre critère : fond
    // d'une section, encre d'une autre, selon qui écrivait en dernier. Quand ce
    // module tourne, l'observateur déclenche ce calcul-ci au lieu du sien.
    if (hasUniverses) window.__memoriaLerp = update;

    // Page Visibility — pause when tab hidden (saves background CPU).
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && raf) {
        cancelAnimationFrame(raf);
        scrollPending = false;
      }
    });
  }

  /* ------------------------------------------------------------------------
     3. REVEALS
  ------------------------------------------------------------------------ */
  function initReveals() {
    if (prefersReduced) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-10% 0px', threshold: 0.05 }
    );
    document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------------------------
     4. MOBILE BURGER / DRAWER
  ------------------------------------------------------------------------ */
  function initMobileNav() {
    const burger = document.querySelector('[data-burger]');
    const mobnav = document.querySelector('[data-mobnav]');
    if (!burger || !mobnav) return;

    function open() {
      burger.classList.add('is-open');
      mobnav.classList.add('is-open');
      mobnav.setAttribute('aria-hidden', 'false');
      // a11y : retirer inert pour rendre les liens focusables quand ouvert.
      mobnav.removeAttribute('inert');
      burger.setAttribute('aria-expanded', 'true');
      burger.setAttribute('aria-label', 'Fermer le menu');
      document.body.classList.add('menu-open');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      burger.classList.remove('is-open');
      mobnav.classList.remove('is-open');
      mobnav.setAttribute('aria-hidden', 'true');
      // a11y : inert empêche le focus clavier sur les liens du menu fermé.
      mobnav.setAttribute('inert', '');
      burger.setAttribute('aria-expanded', 'false');
      burger.setAttribute('aria-label', 'Ouvrir le menu');
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    }

    burger.addEventListener('click', () => {
      if (mobnav.classList.contains('is-open')) close();
      else open();
    });

    mobnav.querySelectorAll('[data-mobnav-link]').forEach((link) => {
      link.addEventListener('click', () => close());
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobnav.classList.contains('is-open')) close();
    });
  }

  /* ------------------------------------------------------------------------
     4b. DESKTOP NAV DROPDOWN (Collection) — aria state + keyboard
     ------------------------------------------------------------------------
     The dropdown panel is revealed via CSS (:hover / :focus-within). This
     enhancement keeps aria-expanded in sync for screen readers and adds
     Escape-to-close + outside-pointer-close. The toggle remains a real link
     (works with JS off). Mobile uses native <details> — no JS needed.
  ------------------------------------------------------------------------ */
  function initNavDropdowns() {
    const items = document.querySelectorAll('[data-dropdown]');
    if (!items.length) return;

    items.forEach((item) => {
      const toggle = item.querySelector('[data-dropdown-toggle]');
      if (!toggle) return;

      const setOpen = (open) => {
        item.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      // Pointer (mouse) open/close on the whole item.
      item.addEventListener('pointerenter', () => setOpen(true));
      item.addEventListener('pointerleave', () => setOpen(false));

      // Keyboard: opens as soon as the toggle or any panel link is focused,
      // closes when focus leaves the item entirely.
      item.addEventListener('focusin', () => setOpen(true));
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) setOpen(false);
      });

      // Escape returns focus to the toggle and closes.
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          setOpen(false);
          toggle.focus();
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     5. CART — quick-add from product card (progressive enhancement)
     For Sprint 1 we only handle the visible "+ Réserver" pill. It POSTs to
     /cart/add.js asynchronously and updates the header cart counter.
     If JS is off, the user simply lands on the product page (link behavior).
  ------------------------------------------------------------------------ */
  function initQuickReserve() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.pcard__quick-btn');
      if (!btn) return;
      // Only the "+ Réserver" variant (not ghost "Voir la fiche")
      if (btn.classList.contains('pcard__quick-btn--ghost')) return;

      const card = btn.closest('.pcard');
      if (!card) return;

      // Extract handle from card href, then fetch variant id via products.json
      const href = card.getAttribute('href');
      if (!href) return;
      const match = href.match(/\/products\/([^/?#]+)/);
      if (!match) return;
      const handle = match[1];

      e.preventDefault();
      e.stopPropagation();

      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Réservation…';

      try {
        const productRes = await fetch(`/products/${handle}.js`);
        if (!productRes.ok) throw new Error('product fetch failed');
        const product = await productRes.json();
        const variantId = product.variants && product.variants[0] && product.variants[0].id;
        if (!variantId) throw new Error('no variant');

        const cartRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (!cartRes.ok) throw new Error('cart add failed');

        // Update header counter
        const countRes = await fetch('/cart.js');
        if (countRes.ok) {
          const cart = await countRes.json();
          document.querySelectorAll('[data-cart-count]').forEach((n) => {
            n.textContent = cart.item_count;
          });
        }

        btn.textContent = 'Réservé ✓';
        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
        }, 1800);
      } catch (err) {
        console.warn('[memoria] reserve failed', err);
        // Fallback: navigate to the product page so the user can still buy
        window.location.href = href;
      }
    });
  }

  /* ------------------------------------------------------------------------
     7. COLLECTION FILTERS — auto-submit sort, close advanced on outside click
  ------------------------------------------------------------------------ */
  function initCollectionFilters() {
    // Auto-submit du select tri (sort_by) au change — progressive enhancement.
    // Sans JS, le <noscript><button type="submit"></noscript> dans le form
    // fonctionne aussi.
    document.querySelectorAll('[data-collection-sort]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });

    // Ferme le <details> "Filtres avancés" si clic en dehors (UX classique
    // de dropdown). Le panel est un détails natif donc accessible au clavier
    // et fonctionne sans JS.
    document.querySelectorAll('[data-collection-advanced]').forEach((details) => {
      document.addEventListener('click', (e) => {
        if (!details.open) return;
        if (!details.contains(e.target)) {
          details.removeAttribute('open');
        }
      });
      // Escape ferme aussi
      details.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && details.open) {
          details.removeAttribute('open');
          const summary = details.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     8. PRODUCT PAGE (PDP)
     - Gallery: click thumb -> swap main image with crossfade
     - Variant selectors (Bracelet/Taille/...): update hidden input + price +
       URL ?variant=XXX + CTA label
     - Engrave + Message char counters (with aria-live, near-limit class)
     - Form submit: native Shopify POST /cart/add (no JS fetch — works without JS)
  ------------------------------------------------------------------------ */
  function initProductPage() {
    const pdpRoot = document.querySelector('[data-product-page]');
    if (!pdpRoot) return;

    /* ---- Product JSON (variants, options, prices) ---- */
    let productData = null;
    const jsonEl = document.getElementById('pdp-product-json');
    if (jsonEl) {
      try {
        productData = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.warn('[memoria] product JSON parse failed', e);
      }
    }

    /* ---- Gallery: thumb swap ---- */
    const mainImg = document.getElementById('pdp-main-img');
    const thumbs = document.querySelectorAll('[data-pdp-thumb]');

    function swapMainImage(thumb) {
      if (!mainImg) return;
      const newSrc = thumb.dataset.src;
      const newSrcset = thumb.dataset.srcset;
      const newAlt = thumb.dataset.alt || mainImg.alt;
      if (!newSrc) return;
      if (prefersReduced) {
        mainImg.src = newSrc;
        if (newSrcset) mainImg.srcset = newSrcset;
        mainImg.alt = newAlt;
      } else {
        mainImg.classList.add('is-swapping');
        const finish = () => {
          mainImg.src = newSrc;
          if (newSrcset) mainImg.srcset = newSrcset;
          mainImg.alt = newAlt;
          // Force reflow then remove swapping class to fade back in
          // (use rAF to avoid blocking the fade-out frame).
          requestAnimationFrame(() => {
            mainImg.classList.remove('is-swapping');
          });
        };
        // Wait for fade-out (320ms in CSS) before swapping src.
        setTimeout(finish, 280);
      }
      thumbs.forEach((t) => {
        const isCurrent = t === thumb;
        t.classList.toggle('is-current', isCurrent);
        t.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => swapMainImage(thumb));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          swapMainImage(thumb);
        }
      });
    });

    /* ---- Variant selectors ---- */
    const form = document.querySelector('[data-pdp-form]');
    const variantInput = document.querySelector('[data-pdp-variant-id]');
    const priceMain = document.querySelector('[data-pdp-price]');
    const submitPrice = document.querySelector('[data-pdp-submit-price]');
    const submitBtn = document.querySelector('[data-pdp-submit]');
    const stockEl = document.querySelector('[data-pdp-stock]');
    const optionInputs = document.querySelectorAll('[data-pdp-option-input]');

    function getCurrentSelection() {
      if (!productData) return null;
      const selected = [];
      productData.options.forEach((optName) => {
        const input = form.querySelector(`input[name="options[${cssEscape(optName)}]"]:checked`);
        selected.push(input ? input.value : null);
      });
      return selected;
    }

    function cssEscape(s) {
      if (window.CSS && CSS.escape) return CSS.escape(s);
      return String(s).replace(/["\\]/g, '\\$&');
    }

    function findVariantByOptions(selected) {
      if (!productData || !selected) return null;
      return productData.variants.find((v) => {
        if (!v.options || v.options.length !== selected.length) return false;
        for (let i = 0; i < selected.length; i++) {
          if (selected[i] != null && v.options[i] !== selected[i]) return false;
        }
        return true;
      });
    }

    function formatMoney(cents) {
      // Reproduce Shopify's money_without_trailing_zeros filter in EUR.
      // €250 / €249,50 etc.
      const v = (cents || 0) / 100;
      const hasDecimals = v % 1 !== 0;
      const formatted = hasDecimals
        ? v.toFixed(2).replace('.', ',') + ' €'
        : Math.round(v) + ' €';
      return formatted;
    }

    function updateVariant() {
      const selected = getCurrentSelection();
      const variant = findVariantByOptions(selected);
      if (!variant) return;

      // Update hidden input
      if (variantInput) variantInput.value = variant.id;

      // Update URL query param (?variant=XXX) without reload
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        /* ignore */
      }

      // Update price (main + CTA)
      const priceStr = formatMoney(variant.price);
      if (priceMain) priceMain.textContent = priceStr;
      if (submitPrice) submitPrice.textContent = priceStr;

      // Update CTA enabled state
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
      }

      // Update stock display
      if (stockEl) {
        stockEl.classList.remove('ok', 'low', 'out');
        const labelEl = stockEl.querySelector('.pdp-stock__label');
        if (!variant.available) {
          stockEl.classList.add('out');
          if (labelEl) labelEl.textContent = '○ Indisponible';
        } else if (variant.inventory_quantity > 0 && variant.inventory_quantity < 3) {
          stockEl.classList.add('low');
          if (labelEl) labelEl.textContent = `● Plus que ${variant.inventory_quantity} disponibles`;
        } else {
          stockEl.classList.add('ok');
          if (labelEl) labelEl.textContent = '● Disponible · Assemblage 15-20 jours';
        }
      }

      // Update gallery if variant has a specific image
      // The variant.featured_image.src is on Shopify CDN ; we look for the
      // matching thumb by URL filename root (avoids matching on width transforms).
      if (variant.featured_image && variant.featured_image.src && mainImg) {
        const vSrc = variant.featured_image.src.split('?')[0];
        const vFileRoot = vSrc.split('/').pop().split('.')[0];
        const matching = Array.from(thumbs).find((t) => {
          const tSrc = t.dataset.src || '';
          return tSrc.indexOf(vFileRoot) >= 0;
        });
        if (matching) swapMainImage(matching);
      }
    }

    optionInputs.forEach((input) => {
      input.addEventListener('change', updateVariant);
    });

    /* ---- Engrave + Message char counters ---- */
    function bindCounter(inputSel, countSel, max) {
      const input = document.querySelector(inputSel);
      const count = document.querySelector(countSel);
      if (!input || !count) return;
      const counterWrap = count.closest('.pdp-engrave__counter');
      const sync = () => {
        const len = input.value.length;
        count.textContent = len;
        if (counterWrap) {
          if (len >= max * 0.85) {
            counterWrap.setAttribute('data-near-limit', 'true');
          } else {
            counterWrap.removeAttribute('data-near-limit');
          }
        }
      };
      input.addEventListener('input', sync);
      sync();
    }
    bindCounter('[data-pdp-engrave]', '[data-pdp-engrave-count]', 20);
    bindCounter('[data-pdp-message]', '[data-pdp-message-count]', 200);

    /* ---- Form submit progressive enhancement ----
       Native POST works without JS (Shopify cart/add). With JS, we could fetch
       /cart/add.js for an inline cart drawer — V1 keeps it simple : let
       Shopify navigate to /cart. */
  }

  /* ------------------------------------------------------------------------
     9. PAGE ATELIER (templates/page.atelier.liquid)
     - Parallaxe légère sur le hero visuel (translateY * 0.18)
     - Count-up sur les 4 stats (RAF ease-out cubic, déclenché au reveal)
     - Stagger reveals sur le process (déjà géré côté CSS via data-step)
     - prefers-reduced-motion : parallaxe OFF, count-up → résultat final direct
  ------------------------------------------------------------------------ */
  function initAtelierPage() {
    const root = document.querySelector('[data-atelier-page]');
    if (!root) return;

    /* ---- Universe lerp via IntersectionObserver (robust fallback) ----
       Le scroll listener du module 1 (initThreadAndLerp) ne se déclenche
       pas toujours sur cette page (Shopify preview wrapper, overflow:clip
       cascade qui promeut body en scroll container, etc.). Cet observer
       garantit que --bg/--fg/--line changent quand la section visible
       change, sans dépendre des scroll events. CSS transition (atelier-extras.css)
       lisse le changement. */
    if (UNIVERSES.length) {
      const universesById = {};
      UNIVERSES.forEach(u => { universesById[u.id] = u; });
      const sections = document.querySelectorAll('[data-universe]');
      if (sections.length) {
        const docEl = document.documentElement;
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        const thread = document.querySelector('[data-thread]');
        const visibilityMap = new Map();
        let currentUniverseId = null;
        const applyUniverse = (universe) => {
          if (window.__memoriaLerp) { window.__memoriaLerp(); return; }
          if (!universe || universe.id === currentUniverseId) return;
          currentUniverseId = universe.id;
          docEl.style.setProperty('--bg', universe.bg);
          docEl.style.setProperty('--fg', universe.fg);
          docEl.style.setProperty('--fg-soft', universe.fg + 'b3'); // ~70%
          docEl.style.setProperty('--fg-mute', universe.fg + '94'); // ~58% (WCAG AA)
          docEl.style.setProperty('--rule', universe.fg + '24');    // ~14%
          docEl.style.setProperty('--line', universe.line);
          if (thread) thread.style.color = universe.line;
          if (themeMeta) themeMeta.setAttribute('content', universe.bg);
        };
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(e => visibilityMap.set(e.target, e.intersectionRatio));
          let bestSection = null;
          let bestRatio = 0;
          visibilityMap.forEach((ratio, section) => {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              bestSection = section;
            }
          });
          if (bestSection) {
            applyUniverse(universesById[bestSection.dataset.universe]);
          }
        }, { threshold: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1] });
        sections.forEach(s => observer.observe(s));
      }
    }

    /* ---- Parallaxe légère sur le hero visuel ---- */
    const parallaxEl = root.querySelector('[data-atelier-parallax]');
    if (parallaxEl && !prefersReduced) {
      let rafId = null;
      let pending = false;
      const factor = 0.14;
      const update = () => {
        const rect = parallaxEl.getBoundingClientRect();
        const vh = window.innerHeight;
        // Only translate while in/near the viewport (perf : skip when off-screen)
        if (rect.bottom < -200 || rect.top > vh + 200) {
          pending = false;
          return;
        }
        // Compute offset relative to viewport center → smoother result
        const center = rect.top + rect.height / 2;
        const dy = (center - vh / 2) * -factor;
        parallaxEl.style.transform = `translate3d(0, ${dy.toFixed(2)}px, 0)`;
        pending = false;
      };
      const onScroll = () => {
        if (pending) return;
        pending = true;
        rafId = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      // Initial paint
      update();
    }

    /* ---- Count-up sur les stats ---- */
    const counters = root.querySelectorAll('[data-counter]');
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    function runCounter(el) {
      const endRaw = el.getAttribute('data-counter-end');
      const end = Number(endRaw);
      if (!Number.isFinite(end)) return;
      const duration = Number(el.getAttribute('data-counter-duration')) || 1500;
      const suffix = el.getAttribute('data-counter-suffix') || '';
      const isFloat = !Number.isInteger(end);

      if (prefersReduced) {
        el.textContent = (isFloat ? end.toFixed(1) : Math.round(end)) + suffix;
        return;
      }

      const start = performance.now();
      function tick(now) {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / duration);
        const eased = easeOutCubic(t);
        const value = end * eased;
        el.textContent = (isFloat ? value.toFixed(1) : Math.round(value)) + suffix;
        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          // Final exact value
          el.textContent = (isFloat ? end.toFixed(1) : Math.round(end)) + suffix;
        }
      }
      requestAnimationFrame(tick);
    }

    if (counters.length && 'IntersectionObserver' in window) {
      const counterIO = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              runCounter(entry.target);
              counterIO.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 }
      );
      counters.forEach((el) => counterIO.observe(el));
    } else {
      // Fallback : run immediately
      counters.forEach(runCounter);
    }

    /* ---- Process stagger : assign --step-index for CSS delay ----
       Already encoded in data-step attribute, but we surface it as
       a CSS var so the delay can compose with other reveal logic. */
    root.querySelectorAll('[data-step]').forEach((el) => {
      const idx = Number(el.getAttribute('data-step')) || 0;
      el.style.setProperty('--step-index', String(Math.max(0, idx - 1)));
    });
  }

  /* ------------------------------------------------------------------------
     10. PAGE NOTRE HISTOIRE (templates/page.notre-histoire.liquid)
     - Lerp 7 univers via IntersectionObserver (même pattern qu'atelier)
     - Parallaxe légère sur le hero visuel (translateY * 0.14)
     - prefers-reduced-motion : parallaxe OFF, transitions body OFF (CSS)
  ------------------------------------------------------------------------ */
  function initHistoirePage() {
    const root = document.querySelector('[data-histoire-page]');
    if (!root) return;

    /* ---- Universe lerp via IntersectionObserver (même pattern qu'atelier).
       Le wrapper .page-histoire et .page-histoire .u sont transparents — c'est
       le body qui porte le bg du lerp. ---- */
    if (UNIVERSES.length) {
      const universesById = {};
      UNIVERSES.forEach(u => { universesById[u.id] = u; });
      const sections = document.querySelectorAll('[data-universe]');
      if (sections.length) {
        const docEl = document.documentElement;
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        const thread = document.querySelector('[data-thread]');
        const visibilityMap = new Map();
        let currentUniverseId = null;
        const applyUniverse = (universe) => {
          if (window.__memoriaLerp) { window.__memoriaLerp(); return; }
          if (!universe || universe.id === currentUniverseId) return;
          currentUniverseId = universe.id;
          docEl.style.setProperty('--bg', universe.bg);
          docEl.style.setProperty('--fg', universe.fg);
          docEl.style.setProperty('--fg-soft', universe.fg + 'b3');
          docEl.style.setProperty('--fg-mute', universe.fg + '94');
          docEl.style.setProperty('--rule', universe.fg + '24');
          docEl.style.setProperty('--line', universe.line);
          if (thread) thread.style.color = universe.line;
          if (themeMeta) themeMeta.setAttribute('content', universe.bg);
        };
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(e => visibilityMap.set(e.target, e.intersectionRatio));
          let bestSection = null;
          let bestRatio = 0;
          visibilityMap.forEach((ratio, section) => {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              bestSection = section;
            }
          });
          if (bestSection) {
            applyUniverse(universesById[bestSection.dataset.universe]);
          }
        }, { threshold: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1] });
        sections.forEach(s => observer.observe(s));
      }
    }

    /* ---- Parallaxe légère sur le hero visuel ---- */
    const parallaxEl = root.querySelector('[data-histoire-parallax]');
    if (parallaxEl && !prefersReduced) {
      let rafId = null;
      let pending = false;
      const factor = 0.14;
      const update = () => {
        const rect = parallaxEl.getBoundingClientRect();
        const vh = window.innerHeight;
        if (rect.bottom < -200 || rect.top > vh + 200) {
          pending = false;
          return;
        }
        const center = rect.top + rect.height / 2;
        const dy = (center - vh / 2) * -factor;
        parallaxEl.style.transform = `translate3d(0, ${dy.toFixed(2)}px, 0)`;
        pending = false;
      };
      const onScroll = () => {
        if (pending) return;
        pending = true;
        rafId = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    /* ---- Stagger reveals via --step-index pour les ancres ---- */
    root.querySelectorAll('[data-step]').forEach((el) => {
      const idx = Number(el.getAttribute('data-step')) || 0;
      el.style.setProperty('--step-index', String(Math.max(0, idx - 1)));
    });
  }

  /* ------------------------------------------------------------------------
     11. PAGE MOD-EXPLIQUE (templates/page.mod-explique.liquid)
     - Lerp d'univers via IntersectionObserver (même pattern qu'atelier/histoire)
     - Count-up sur les 4 stats NH35 (RAF, format "21 600" pour le grand chiffre)
     - Scroll-driven exploded view (anatomie) : section.--scroll-progress 0→1
     - Stagger reveals via --step-index
     - prefers-reduced-motion : exploded view désactivé (pièces compactées),
       count-up → résultat direct, transitions body OFF (CSS)
  ------------------------------------------------------------------------ */
  function initModExpliquePage() {
    const root = document.querySelector('[data-mod-explique-page]');
    if (!root) return;

    /* ---- Universe lerp via IntersectionObserver (même pattern) ---- */
    if (UNIVERSES.length) {
      const universesById = {};
      UNIVERSES.forEach((u) => {
        universesById[u.id] = u;
      });
      const sections = document.querySelectorAll('[data-universe]');
      if (sections.length) {
        const docEl = document.documentElement;
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        const thread = document.querySelector('[data-thread]');
        const visibilityMap = new Map();
        let currentUniverseId = null;
        const applyUniverse = (universe) => {
          if (window.__memoriaLerp) { window.__memoriaLerp(); return; }
          if (!universe || universe.id === currentUniverseId) return;
          currentUniverseId = universe.id;
          docEl.style.setProperty('--bg', universe.bg);
          docEl.style.setProperty('--fg', universe.fg);
          docEl.style.setProperty('--fg-soft', universe.fg + 'b3');
          docEl.style.setProperty('--fg-mute', universe.fg + '94');
          docEl.style.setProperty('--rule', universe.fg + '24');
          docEl.style.setProperty('--line', universe.line);
          if (thread) thread.style.color = universe.line;
          if (themeMeta) themeMeta.setAttribute('content', universe.bg);
        };
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => visibilityMap.set(e.target, e.intersectionRatio));
            let bestSection = null;
            let bestRatio = 0;
            visibilityMap.forEach((ratio, section) => {
              if (ratio > bestRatio) {
                bestRatio = ratio;
                bestSection = section;
              }
            });
            if (bestSection) {
              applyUniverse(universesById[bestSection.dataset.universe]);
            }
          },
          { threshold: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1] }
        );
        sections.forEach((s) => observer.observe(s));
      }
    }

    /* ---- Stagger via --step-index ---- */
    root.querySelectorAll('[data-step]').forEach((el) => {
      const idx = Number(el.getAttribute('data-step')) || 0;
      el.style.setProperty('--step-index', String(Math.max(0, idx - 1)));
    });

    /* ---- Count-up sur les 4 stats NH35 ---- */
    const counters = root.querySelectorAll('[data-counter]');
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    function formatValue(val, format) {
      if (format === 'space') {
        // Format "21 600" : insère espace insécable tous les 3 chiffres depuis la droite
        return Math.round(val).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      }
      return Math.round(val).toString();
    }

    function runCounter(el) {
      const endRaw = el.getAttribute('data-counter-end');
      const end = Number(endRaw);
      if (!Number.isFinite(end)) return;
      const duration = Number(el.getAttribute('data-counter-duration')) || 1500;
      const format = el.getAttribute('data-counter-format') || '';

      if (prefersReduced) {
        el.textContent = formatValue(end, format);
        return;
      }

      const start = performance.now();
      function tick(now) {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / duration);
        const eased = easeOutCubic(t);
        const value = end * eased;
        el.textContent = formatValue(value, format);
        if (t < 1) {
          requestAnimationFrame(tick);
        } else {
          el.textContent = formatValue(end, format);
        }
      }
      requestAnimationFrame(tick);
    }

    if (counters.length && 'IntersectionObserver' in window) {
      const counterIO = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              runCounter(entry.target);
              counterIO.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 }
      );
      counters.forEach((el) => counterIO.observe(el));
    } else {
      counters.forEach(runCounter);
    }

    /* ---- Scroll-driven exploded view (section 3 — anatomie) ---- */
    const explodedSection = root.querySelector('[data-exploded-view]');
    if (explodedSection && !prefersReduced) {
      let pending = false;
      let lastProgress = -1;
      const update = () => {
        const rect = explodedSection.getBoundingClientRect();
        const vh = window.innerHeight;
        // Progress 0→1 quand la section traverse le viewport
        const sectionH = rect.height;
        const start = vh; // début : section entre dans le viewport (top à vh)
        const end = -sectionH; // fin : section sort par le haut (top à -sectionH)
        const raw = (start - rect.top) / (start - end);
        const progress = Math.max(0, Math.min(1, raw));
        // Focus : zone d'explosion concentrée entre 0.2 → 0.8 (pic 0.5)
        const focusRange = 0.6; // 0.2 → 0.8
        const focusStart = 0.2;
        const focused = Math.max(0, Math.min(1, (progress - focusStart) / focusRange));
        // Bell-curve : 0 → 1 → 0 (sin pi*t) pour explosion + re-compactage
        const t = Math.sin(focused * Math.PI);
        const display = Number(t.toFixed(3));
        if (display !== lastProgress) {
          explodedSection.style.setProperty('--scroll-progress', String(display));
          lastProgress = display;
        }
        pending = false;
      };
      const onScroll = () => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(update);
      };
      // Bind to multiple scroll sources (window + document + body) per
      // theme.js convention — some Shopify previews scroll via document.
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }
  }

  /* ------------------------------------------------------------------------
     12. PAGE JOURNAL (templates/blog.liquid — page liste du blog)
     - Lerp d'univers via IntersectionObserver (même pattern que histoire)
     - Stagger reveals via --step-index (déjà calé côté CSS .acard, mais on
       force aussi côté JS pour la pagination/featured)
     - prefers-reduced-motion : transitions body OFF (CSS)
  ------------------------------------------------------------------------ */
  function initJournalPage() {
    const root = document.querySelector('[data-journal-page]');
    if (!root) return;

    /* ---- Universe lerp via IntersectionObserver ---- */
    if (UNIVERSES.length) {
      const universesById = {};
      UNIVERSES.forEach((u) => { universesById[u.id] = u; });
      const sections = document.querySelectorAll('[data-universe]');
      if (sections.length) {
        const docEl = document.documentElement;
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        const thread = document.querySelector('[data-thread]');
        const visibilityMap = new Map();
        let currentUniverseId = null;
        const applyUniverse = (universe) => {
          if (window.__memoriaLerp) { window.__memoriaLerp(); return; }
          if (!universe || universe.id === currentUniverseId) return;
          currentUniverseId = universe.id;
          docEl.style.setProperty('--bg', universe.bg);
          docEl.style.setProperty('--fg', universe.fg);
          docEl.style.setProperty('--fg-soft', universe.fg + 'b3');
          docEl.style.setProperty('--fg-mute', universe.fg + '94');
          docEl.style.setProperty('--rule', universe.fg + '24');
          docEl.style.setProperty('--line', universe.line);
          if (thread) thread.style.color = universe.line;
          if (themeMeta) themeMeta.setAttribute('content', universe.bg);
        };
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => visibilityMap.set(e.target, e.intersectionRatio));
            let bestSection = null;
            let bestRatio = 0;
            visibilityMap.forEach((ratio, section) => {
              if (ratio > bestRatio) {
                bestRatio = ratio;
                bestSection = section;
              }
            });
            if (bestSection) {
              applyUniverse(universesById[bestSection.dataset.universe]);
            }
          },
          { threshold: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1] }
        );
        sections.forEach((s) => observer.observe(s));
      }
    }

    /* ---- Stagger via --step-index ---- */
    root.querySelectorAll('[data-step]').forEach((el) => {
      const idx = Number(el.getAttribute('data-step')) || 0;
      el.style.setProperty('--step-index', String(Math.max(0, idx - 1)));
    });
  }

  /* ------------------------------------------------------------------------
     13. PAGE ARTICLE (templates/article.liquid — page individuelle)
     - Lerp d'univers via IntersectionObserver (idem journal/histoire)
     - Reading progress bar fixed top (scroll progress 0→1 du data-article-body)
     - Sticky TOC desktop (>= 1024px) : généré depuis les h2 du body, highlight au scroll
     - Parallax léger sur l'image hero (data-article-parallax)
     - prefers-reduced-motion : parallax OFF, transitions body OFF
  ------------------------------------------------------------------------ */
  function initArticlePage() {
    const root = document.querySelector('[data-article-page]');
    if (!root) return;

    /* ---- Universe lerp ---- */
    if (UNIVERSES.length) {
      const universesById = {};
      UNIVERSES.forEach((u) => { universesById[u.id] = u; });
      const sections = document.querySelectorAll('[data-universe]');
      if (sections.length) {
        const docEl = document.documentElement;
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        const thread = document.querySelector('[data-thread]');
        const visibilityMap = new Map();
        let currentUniverseId = null;
        const applyUniverse = (universe) => {
          if (window.__memoriaLerp) { window.__memoriaLerp(); return; }
          if (!universe || universe.id === currentUniverseId) return;
          currentUniverseId = universe.id;
          docEl.style.setProperty('--bg', universe.bg);
          docEl.style.setProperty('--fg', universe.fg);
          docEl.style.setProperty('--fg-soft', universe.fg + 'b3');
          docEl.style.setProperty('--fg-mute', universe.fg + '94');
          docEl.style.setProperty('--rule', universe.fg + '24');
          docEl.style.setProperty('--line', universe.line);
          if (thread) thread.style.color = universe.line;
          if (themeMeta) themeMeta.setAttribute('content', universe.bg);
        };
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => visibilityMap.set(e.target, e.intersectionRatio));
            let bestSection = null;
            let bestRatio = 0;
            visibilityMap.forEach((ratio, section) => {
              if (ratio > bestRatio) {
                bestRatio = ratio;
                bestSection = section;
              }
            });
            if (bestSection) {
              applyUniverse(universesById[bestSection.dataset.universe]);
            }
          },
          { threshold: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1] }
        );
        sections.forEach((s) => observer.observe(s));
      }
    }

    /* ---- Reading progress bar ---- */
    const progressEl = root.querySelector('[data-article-progress]');
    const articleBody = root.querySelector('[data-article-body]');
    if (progressEl && articleBody) {
      let pendingProg = false;
      let lastProg = -1;
      const updateProgress = () => {
        const rect = articleBody.getBoundingClientRect();
        const vh = window.innerHeight;
        // total scrollable distance = body height - viewport (only meaningful if positive)
        const total = rect.height - vh;
        let progress = 0;
        if (total > 0) {
          progress = Math.max(0, Math.min(1, -rect.top / total));
        }
        const rounded = Math.round(progress * 1000) / 1000;
        if (rounded !== lastProg) {
          progressEl.style.setProperty('--article-progress', String(rounded));
          lastProg = rounded;
        }
        pendingProg = false;
      };
      const onScrollProg = () => {
        if (pendingProg) return;
        pendingProg = true;
        requestAnimationFrame(updateProgress);
      };
      window.addEventListener('scroll', onScrollProg, { passive: true });
      document.addEventListener('scroll', onScrollProg, { passive: true });
      window.addEventListener('resize', onScrollProg, { passive: true });
      updateProgress();
    }

    /* ---- Sticky TOC (desktop) ---- */
    const tocEl = root.querySelector('[data-article-toc]');
    if (tocEl && articleBody && window.matchMedia('(min-width: 1024px)').matches) {
      const headings = Array.from(articleBody.querySelectorAll('h2'));
      if (headings.length >= 3) {
        const list = tocEl.querySelector('.u-article-body__toc-list');
        if (list) {
          headings.forEach((h, idx) => {
            if (!h.id) {
              h.id = 'h-' + idx + '-' + (h.textContent || '').toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                .slice(0, 48);
            }
            const li = document.createElement('li');
            li.className = 'u-article-body__toc-item';
            const a = document.createElement('a');
            a.href = '#' + h.id;
            a.className = 'u-article-body__toc-link';
            a.textContent = h.textContent || '';
            a.dataset.tocTarget = h.id;
            li.appendChild(a);
            list.appendChild(li);
          });
          tocEl.removeAttribute('hidden');

          /* highlight au scroll */
          const tocLinks = Array.from(list.querySelectorAll('.u-article-body__toc-link'));
          const hMap = new Map();
          headings.forEach((h) => hMap.set(h.id, h));

          const setActive = (id) => {
            tocLinks.forEach((l) => {
              l.classList.toggle('is-active', l.dataset.tocTarget === id);
            });
          };

          const tocObserver = new IntersectionObserver(
            (entries) => {
              const visible = entries
                .filter((e) => e.isIntersecting)
                .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
              if (visible.length) {
                setActive(visible[0].target.id);
              }
            },
            { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
          );
          headings.forEach((h) => tocObserver.observe(h));
        }
      }
    }

    /* ---- Parallax léger sur l'image hero ---- */
    const parallaxEl = root.querySelector('[data-article-parallax]');
    if (parallaxEl && !prefersReduced) {
      let pendingPx = false;
      const factor = 0.1;
      const updatePx = () => {
        const rect = parallaxEl.getBoundingClientRect();
        const vh = window.innerHeight;
        if (rect.bottom < -200 || rect.top > vh + 200) {
          pendingPx = false;
          return;
        }
        const center = rect.top + rect.height / 2;
        const dy = (center - vh / 2) * -factor;
        parallaxEl.style.transform = `translate3d(0, ${dy.toFixed(2)}px, 0)`;
        pendingPx = false;
      };
      const onScrollPx = () => {
        if (pendingPx) return;
        pendingPx = true;
        requestAnimationFrame(updatePx);
      };
      window.addEventListener('scroll', onScrollPx, { passive: true });
      window.addEventListener('resize', onScrollPx, { passive: true });
      updatePx();
    }

    /* ---- Stagger via --step-index ---- */
    root.querySelectorAll('[data-step]').forEach((el) => {
      const idx = Number(el.getAttribute('data-step')) || 0;
      el.style.setProperty('--step-index', String(Math.max(0, idx - 1)));
    });
  }

  /* ------------------------------------------------------------------------
     LOGO SWAP — fallback pour les pages SANS lerp (PDP, collection, contact…)
     ----------------------------------------------------------------------------
     Sur ces pages, update() du module lerp `return` avant de calculer `bg`
     (pas de sections [data-universe]) — voire initThreadAndLerp() retourne tôt
     si UNIVERSES est vide. On lit donc la couleur de fond calculée du <body>
     une fois et on pose `header--on-dark` selon sa luminance.
     Sur la home, ce toggle initial est ensuite repris par update() au scroll.
  ------------------------------------------------------------------------ */
  function initLogoSwap() {
    const header = document.getElementById('site-header');
    if (!header) return;

    function applyFromComputed() {
      const cs = getComputedStyle(document.body).backgroundColor || '';
      const m = cs.match(/(\d+(?:\.\d+)?)/g);
      if (!m || m.length < 3) return;
      const r = parseFloat(m[0]),
        g = parseFloat(m[1]),
        b = parseFloat(m[2]),
        alpha = m.length >= 4 ? parseFloat(m[3]) : 1;
      // Fond transparent (rare) → on n'a pas d'info fiable, on s'abstient.
      if (alpha === 0) return;
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      header.classList.toggle('header--on-dark', lum < 0.5);
    }

    // Le module lerp a déjà fait son paint initial (--bg posé) avant cet appel.
    applyFromComputed();
  }

  /* ------------------------------------------------------------------------
     INIT
  ------------------------------------------------------------------------ */
  function init() {
    initThreadAndLerp();
    initLogoSwap();
    initReveals();
    initMobileNav();
    initNavDropdowns();
    initQuickReserve();
    initCollectionFilters();
    initProductPage();
    initAtelierPage();
    initHistoirePage();
    initModExpliquePage();
    initJournalPage();
    initArticlePage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
